import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { PersonnelContext } from 'features/Responder/features/Personnel';
import { useContext, useEffect, useState } from 'react';
import { FaCrown, FaEllipsisV, FaPlus, FaTimes, FaUser, FaUserShield } from 'react-icons/fa';
import { getPersonId, getPersonName } from '../utils/messengerPeopleUtils';
import { getAvailableRoleActions, getCurrentUserRole, getRoleLabel } from '../utils/permissions';
import { Avatar } from './Avatar';

/**
 * Participant list with role management for group conversations.
 * Shows participant roles and allows owners/admins to manage them.
 */
export function ParticipantManager({
  conversation,
  currentUserId,
  onUpdateRole,
  onTransferOwnership,
  onRemoveParticipant,
  onAddParticipant,
}) {
  const personnelContext = useContext(PersonnelContext);
  const personnel = personnelContext?.personnel || {};

  const [activeMenu, setActiveMenu] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedRole, setSelectedRole] = useState('standard');

  const { role: myRole } = getCurrentUserRole(conversation, currentUserId);

  // Check if personnel is loaded
  const personnelLoaded = Object.keys(personnel).length > 0;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown menu
      if (
        !event.target.closest('.responder-messenger-dropdown-menu') &&
        !event.target.closest('.responder-messenger-participant__actions')
      ) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeMenu]);

  if (!conversation || conversation.type !== 'group') {
    return null;
  }

  const participants = (conversation.participants || []).filter((p) => !p.left_at);

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <FaCrown size={12} className="text-warning" />;
      case 'admin':
        return <FaUserShield size={12} className="text-info" />;
      default:
        return <FaUser size={12} className="text-muted" />;
    }
  };

  const coerceParticipantUserId = (participant) => {
    const rawUserId = getPersonId(participant);
    const numericUserId = Number(rawUserId);
    return Number.isFinite(numericUserId) ? numericUserId : rawUserId;
  };

  const handleAction = async (action, targetParticipant) => {
    const targetUserId = coerceParticipantUserId(targetParticipant);
    if (!targetUserId) return;

    setIsProcessing(true);
    setActiveMenu(null);

    try {
      if (action.action === 'transfer') {
        await onTransferOwnership(targetUserId);
      } else if (action.action === 'remove') {
        await onRemoveParticipant(targetUserId);
      } else {
        await onUpdateRole(targetUserId, action.value);
      }
    } catch (err) {
      console.error('[ParticipantManager] Action failed:', err);
      alert(err.message || 'Failed to perform action');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleMenu = (participantId) => {
    setActiveMenu(activeMenu === participantId ? null : participantId);
  };

  const handleAddParticipantClick = () => {
    setShowAddParticipant(true);
    setSelectedUserId(null);
    setSelectedRole('standard');
  };

  const handleCancelAddParticipant = () => {
    setShowAddParticipant(false);
    setSelectedUserId(null);
    setSelectedRole('standard');
  };

  const handleConfirmAddParticipant = async () => {
    if (!selectedUserId || !onAddParticipant) return;

    setIsProcessing(true);
    try {
      await onAddParticipant(selectedUserId, selectedRole);
      setShowAddParticipant(false);
      setSelectedUserId(null);
      setSelectedRole('standard');
    } catch (err) {
      console.error('[ParticipantManager] Add participant failed:', err);
      alert(err?.response?.data?.message || err.message || 'Failed to add participant');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if user can add participants (owner or admin)
  const canAddParticipants = myRole === 'owner' || myRole === 'admin';

  // Get list of user IDs already in the conversation
  const existingUserIds = new Set(participants.map((p) => getPersonId(p)).filter(Boolean));

  // Filter personnel to exclude current participants
  const availablePersonnel = Object.values(personnel || {})
    .filter((person) => {
      if (!person) return false;
      const personId = getPersonId(person);
      // Exclude participants already in the conversation
      return !existingUserIds.has(personId);
    })
    .sort((a, b) => getPersonName(a).localeCompare(getPersonName(b)));

  const personnelOptions = availablePersonnel.map((person) => ({
    value: getPersonId(person),
    label: getPersonName(person),
  }));

  // Only owners can grant admin, matching the option list the native select rendered.
  const roleOptions = [
    { value: 'standard', label: 'Standard Member' },
    ...(myRole === 'owner' ? [{ value: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <div className="responder-messenger-participants">
      <h6 className="responder-messenger-participants__title">
        Group Members ({participants.length})
      </h6>

      {canAddParticipants && !showAddParticipant && (
        <>
          <button
            type="button"
            className="responder-messenger-add-participant-btn"
            onClick={handleAddParticipantClick}
            disabled={isProcessing || !personnelLoaded || availablePersonnel.length === 0}
            title={
              !personnelLoaded
                ? 'Loading personnel...'
                : availablePersonnel.length === 0
                  ? 'All personnel are already in this conversation'
                  : 'Add a participant to this group'
            }
          >
            <FaPlus size={10} /> {!personnelLoaded ? 'Loading...' : 'Add Participant'}
          </button>
          {personnelLoaded && availablePersonnel.length === 0 && (
            <div className="responder-messenger-info-text">
              All personnel are already in this group
            </div>
          )}
        </>
      )}

      {showAddParticipant && (
        <div className="responder-messenger-add-participant">
          <div className="responder-messenger-add-participant__header">
            <h6>Add Participant</h6>
            <button
              type="button"
              className="responder-messenger-icon-btn"
              onClick={handleCancelAddParticipant}
            >
              <FaTimes size={12} />
            </button>
          </div>

          <div className="responder-messenger-add-participant__content">
            <div className="responder-messenger-add-participant__select">
              <label>Select User</label>
              <BaseSelect
                isSearchable
                selectProps={{
                  'aria-label': 'Select User',
                  options: personnelOptions,
                  value: findSelectOption(personnelOptions, selectedUserId ?? ''),
                  placeholder: 'Choose a user...',
                  isDisabled: isProcessing,
                  onChange: (option) =>
                    setSelectedUserId(option?.value == null ? '' : Number(option.value)),
                }}
              />
            </div>

            <div className="responder-messenger-add-participant__select">
              <label>Role</label>
              <BaseSelect
                selectProps={{
                  'aria-label': 'Role',
                  options: roleOptions,
                  value: findSelectOption(roleOptions, selectedRole),
                  isDisabled: isProcessing || myRole !== 'owner',
                  onChange: (option) => setSelectedRole(option.value),
                }}
              />
              {myRole === 'admin' && (
                <small className="text-muted">Only owners can assign admin role</small>
              )}
            </div>

            <div className="responder-messenger-add-participant__actions">
              <button
                type="button"
                className="responder-messenger-btn responder-messenger-btn--secondary"
                onClick={handleCancelAddParticipant}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="responder-messenger-btn responder-messenger-btn--primary"
                onClick={handleConfirmAddParticipant}
                disabled={!selectedUserId || isProcessing}
              >
                {isProcessing ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="responder-messenger-participants__list">
        {participants.map((participant) => {
          const participantUserId = getPersonId(participant);
          // Look up person from personnel context
          const person = personnel[participantUserId];
          const displayName = person
            ? getPersonName(person)
            : participantUserId
              ? `User ${participantUserId}`
              : 'Unknown user';
          const isMe = `${participantUserId}` === `${currentUserId}`;
          const actions = getAvailableRoleActions(myRole, participant, currentUserId);

          return (
            <div key={participant.id} className="responder-messenger-participant">
              <Avatar label={displayName} />

              <div className="responder-messenger-participant__info">
                <div className="responder-messenger-participant__name">
                  {displayName}
                  {isMe && <span className="text-muted"> (You)</span>}
                </div>
                <div className="responder-messenger-participant__role">
                  {getRoleIcon(participant.role)}
                  <span className="ms-1">{getRoleLabel(participant.role)}</span>
                </div>
              </div>

              {actions.length > 0 && !isProcessing && (
                <div className="responder-messenger-participant__actions">
                  <button
                    type="button"
                    className="responder-messenger-icon-btn"
                    onClick={() => toggleMenu(participant.id)}
                    aria-label="Participant options"
                  >
                    <FaEllipsisV size={12} />
                  </button>

                  {activeMenu === participant.id && (
                    <div className="responder-messenger-dropdown-menu">
                      {actions.map((action) => (
                        <button
                          key={action.value}
                          type="button"
                          className={`responder-messenger-dropdown-item ${
                            action.action === 'remove' ? 'text-danger' : ''
                          }`}
                          onClick={() => handleAction(action, participant)}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
